// OpenInVsCodeButton.tsx — "Open in VS Code": a workspace's Code OSS in a
// browser tab of its own, apart from the editor the Sessions page frames.
//
// One click asks the manager for a launch URL, which also wakes a
// suspended workspace's Pod, and sends a new tab to it (see
// openInNewTab.ts). The button spins while the request is out; a failure
// goes to onError for the caller to show where it has room to. The tab
// itself says when the Pod is still starting and retries on its own.

import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { BsBoxArrowUpRight } from 'react-icons/bs';
import { launchErrorMessage, openInNewTab } from './openInNewTab';
import type { CodeLaunch, OpenTab } from './openInNewTab';

export interface OpenInVsCodeButtonProps {
  workspaceId: string;
  /** The manager call: POST /api/workspaces/{id}/code/launch. */
  launch: (workspaceId: string) => Promise<CodeLaunch>;
  /** A launch is starting: clear any error shown for the last one. */
  onStart?: () => void;
  onError: (message: string) => void;
  disabled?: boolean;
  /** Test seam for window.open. */
  openTab?: OpenTab;
}

/** Tooltip shared by every form of the launch button. */
export const OPEN_IN_VS_CODE_TITLE =
  'Open this workspace in VS Code in a new tab. Wakes the workspace if it is suspended.';

/**
 * The launch itself, for callers that draw their own button (the sidebar's
 * compact ButtonBadge): `pending` while the request is out, `onClick` to
 * start one.
 */
export function useCodeLaunch({
  workspaceId, launch, onStart, onError, openTab,
}: Omit<OpenInVsCodeButtonProps, 'disabled'>): { pending: boolean; onClick: () => void } {
  const [pending, setPending] = useState(false);

  const onClick = () => {
    setPending(true);
    onStart?.();
    // Not awaited before the call: the tab has to open inside the click.
    openInNewTab(() => launch(workspaceId), openTab)
      .catch((err: unknown) => {
        const msg = launchErrorMessage(err);
        console.warn(`open in vs code failed for workspace ${workspaceId}:`, msg);
        onError(msg);
      })
      .finally(() => setPending(false));
  };

  return { pending, onClick };
}

export const OpenInVsCodeButton: React.FC<OpenInVsCodeButtonProps> = ({
  workspaceId, launch, onStart, onError, disabled, openTab,
}) => {
  const { pending, onClick } = useCodeLaunch({ workspaceId, launch, onStart, onError, openTab });

  return (
    <Button
      size="sm"
      variant="outline-info"
      className="d-flex align-items-center gap-1 text-nowrap"
      onClick={onClick}
      disabled={disabled || pending}
      aria-busy={pending}
      aria-label="Open in VS Code"
      title={OPEN_IN_VS_CODE_TITLE}
    >
      {pending ? <Spinner animation="border" size="sm" /> : <BsBoxArrowUpRight aria-hidden />} Open in VS Code
    </Button>
  );
};

export default OpenInVsCodeButton;
